import { cn } from "@/lib/utils";
import BrandLogo from "@/components/BrandLogo";

interface LoaderProps {
  fullScreen?: boolean;
  size?: "sm" | "md" | "lg";
  label?: string;
  className?: string;
}

const sizeMap = {
  sm: { logoH: "h-9",  wrap: "h-[4.5rem] w-[4.5rem]" },
  md: { logoH: "h-14", wrap: "h-24 w-24" },
  lg: { logoH: "h-20", wrap: "h-32 w-32" },
};

const KEYFRAMES = `
  @keyframes bl-sweep    { to { transform: rotate(360deg); } }
  @keyframes bl-counter  { to { transform: rotate(-360deg); } }
  @keyframes bl-logo     { 0%,100% { opacity:1; transform:scale(1); }
                           50%     { opacity:.75; transform:scale(.91); } }
  @keyframes bl-dot      { 0%,80%,100% { transform:scale(0); opacity:.3; }
                           40%         { transform:scale(1);  opacity:1;  } }
`;

export default function Loader({ fullScreen = false, size = "md", label, className }: LoaderProps) {
  const { logoH, wrap } = sizeMap[size];

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-5",
          fullScreen && "min-h-screen bg-background",
          className,
        )}
      >
        <div className={cn("relative flex items-center justify-center", wrap)}>
          {/* Outer conic-gradient sweep ring — "comet tail" */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: "conic-gradient(from 0deg, transparent 60%, hsl(var(--primary)/.85) 100%)",
              mask: "radial-gradient(circle, transparent 56%, black 58%)",
              WebkitMask: "radial-gradient(circle, transparent 56%, black 58%)",
              animation: "bl-sweep .95s linear infinite",
            }}
          />
          {/* Inner counter-rotating faint ring */}
          <div
            className="absolute rounded-full border-primary/15"
            style={{
              inset: "12%",
              borderWidth: "1.5px",
              animation: "bl-counter 2.4s linear infinite",
            }}
          />
          {/* Logo with subtle pulse */}
          <BrandLogo
            className={cn("relative z-10 w-auto object-contain select-none", logoH)}
            style={{ animation: "bl-logo 2s ease-in-out infinite" }}
            draggable={false}
          />
        </div>

        {/* Bouncing dots */}
        <div className="flex items-center gap-[5px]">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block h-[5px] w-[5px] rounded-full bg-primary/60"
              style={{ animation: `bl-dot 1.2s ease-in-out ${i * 0.18}s infinite` }}
            />
          ))}
        </div>

        {label && <p className="text-sm text-muted-foreground">{label}</p>}
      </div>
    </>
  );
}
